import { useEffect, useState } from 'react';
import { Trans } from '../i18n/Trans';
import { ApiHelper } from '../helper/ApiHelper';
import { QuoteTable, type QuoteResult } from '../components/QuoteTable';
import { QuoteProgress, type JobView } from '../components/QuoteProgress';
import { OrderDialog } from '../components/OrderDialog';
import { Modal } from '../components/Modal';

/** 历史列表里的一条（摘要） */
interface HistoryBrief {
  id: number;
  fileName: string;
  count: number;
  priced: number;
  total: number;
  currency: string;
  createdAt: string;
}

interface Me { role: string; name?: string; email?: string }

/** 没跑完的估价任务 id 存这里：刷新、切 tab 回来都能接着看同一个任务 */
const JOB_KEY = 'hip.quoteJob';

/**
 * Tab 1 快速估价。**免登录就能试算**（产品要求：查询/试用不挡人）。
 * 未登录时后端下发脱敏视图（没有成本单价与加价率），下载报价单才要登录。
 */
export function QuickQuotePage(
  { user, onNeedLogin }: { user: Me | null; onNeedLogin: (after?: () => void) => void },
) {
  const canSeeCost = !!user && (user.role === 'admin' || user.role === 'buyer');
  const [err, setErr] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [history, setHistory] = useState<HistoryBrief[] | null>(null);
  const [job, setJob] = useState<JobView | null>(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [fxMeta, setFxMeta] = useState<{ day: string; source: string } | undefined>(undefined);
  const [historyView, setHistoryView] = useState<{ fileName: string; quote: QuoteResult } | null>(null);
  const [ordered, setOrdered] = useState(false);

  // 历史只给自己的：匿名不拉（后端会 401，也不该让他看别人的 BOM）
  const loadHistory = () => {
    if (!user) { setHistory(null); return; }
    ApiHelper.call<{ list: HistoryBrief[] }>('/api/quote/history')
      .then((r) => setHistory(r.list))
      .catch(() => setHistory(null));
  };
  useEffect(loadHistory, [user]);

  // 汇率口径（哪天的、取自哪）——报价表里那个小 icon 要显示它。公开接口，匿名也能拿。
  useEffect(() => {
    ApiHelper.call<{ day: string; source: string }>('/api/settings/rates')
      .then((r) => setFxMeta({ day: r.day, source: r.source }))
      .catch(() => setFxMeta(undefined));
  }, []);

  // 刷新或切走再回来：把上次没跟完的任务接上（jobId 本身就是凭据）
  useEffect(() => {
    const id = localStorage.getItem(JOB_KEY);
    if (id) follow(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 看历史里的某一次估价：**开弹窗**，不要把它塞回页面上方的结果区。
   * 原来是 setQuote —— 用户在页面最底部点「查看」，变化发生在很上面，
   * 他看不见任何反应，只会以为按钮没反应。
   */
  const openHistory = async (id: number) => {
    setErr('');
    try {
      const r = await ApiHelper.call<{ item: { fileName: string; quote: QuoteResult } }>(
        `/api/quote/history/${id}`);
      setHistoryView({ fileName: r.item.fileName, quote: r.item.quote });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  // 轮询一个任务直到出结果。jobId 存 localStorage：切走或刷新回来还能接着看同一个任务。
  const follow = async (id: string) => {
    localStorage.setItem(JOB_KEY, id);
    setBusy(true);
    for (;;) {
      let v: JobView;
      try {
        v = (await ApiHelper.call<{ job: JobView & { quote?: QuoteResult } }>(`/api/quote/job/${id}`)).job;
      } catch (e) {
        // 任务过期/进程重启过：如实说，别让进度条永远转
        setErr(e instanceof Error ? e.message : String(e));
        break;
      }
      setJob(v);
      if (v.status === 'done') {
        const done = v as JobView & { quote?: QuoteResult };
        if (done.quote) setQuote(done.quote);
        setOrdered(false);
        loadHistory();   // 每次上传都会形成一条历史，跑完就刷新列表
        break;
      }
      if (v.status === 'error') {
        setErr(v.msg || Trans.t('job.failed'));
        break;
      }
      await new Promise((r) => setTimeout(r, v.pollMs || 700));
    }
    localStorage.removeItem(JOB_KEY);
    setJob(null);
    setBusy(false);
  };

  // 参数是"这次要估的文件"：手动上传传 file，示例试算传刚取回来的那份。
  // 两条路必须走同一个函数，否则历史留痕、错误处理、按钮态很容易只改一处。
  const run = async (which?: File) => {
    const f = which ?? file;
    if (!f) return;
    setBusy(true);
    setErr('');
    setQuote(null);
    setJob(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      // start 立刻返回 jobId，真正的估价在后端后台跑，这里只负责跟进度
      const r = await ApiHelper.upload<{ job: JobView }>('/api/quote/start', fd);
      setJob(r.job);
      await follow(r.job.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  // 一键试算：把示例 BOM 取回来当成用户刚选的文件，接着走同一条上传估价路径。
  // 文件名也照样落进历史，用户回头能看出这条是示例跑的。
  const runSample = async () => {
    setBusy(true);
    setErr('');
    setQuote(null);
    try {
      const f = await ApiHelper.fetchFile('/api/quote/sample', 'hip_sample_bom.xlsx');
      setFile(f);          // 顺手填进上传框，用户能看到到底在算哪份文件
      await run(f);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);      // run 没跑起来，busy 得自己收
    }
  };

  // 一键下单：弹窗要联系人/电话/地址（发货必需），不静默落单。
  // 没登录就先弹登录，**登录完自动回到下单弹窗**，不用从头再来一遍估价。
  // **下单不需要登录**：查询与下单都是主路径，不挡人。弹窗里填联系方式即可。
  const placeOrder = () => {
    if (!quote) return;
    setErr('');
    setOrderOpen(true);
  };

  const download = async () => {
    if (!quote) return;
    setErr('');
    try {
      await ApiHelper.download('/api/quote/export', { quote }, 'hip_quotation.xlsx');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div>
      {/* 不写标题也不写导语：tab 名已经说了这是"快速估价"，页面第一屏直接给上传框。
          功能说明式的长句子属于首页/文档，摆在操作页顶上只是挡住主操作 */}
      {/* 自己的上传控件：原生 input 藏起来，避免浏览器那个英文 "Choose File" 破相 */}
      <label
        className={'dropzone' + (drag ? ' over' : '')}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer.files?.[0];
          if (f) { setFile(f); setQuote(null); }
        }}
      >
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          className="file-input-hidden"
          onChange={(e) => { setFile(e.target.files?.[0] ?? null); setQuote(null); }}
        />
        <span className="drop-glyph" aria-hidden="true" />
        <div className="dropzone-title">{file ? file.name : Trans.t('quote.upload')}</div>
        <div className="hint">
          {file
            ? `${(file.size / 1024).toFixed(0)} KB · ${Trans.t('quote.reselect')}`
            : Trans.t('quote.uploadHint')}
        </div>
        <span className="btn ghost pick">{Trans.t('quote.pick')}</span>
      </label>

      {/* 空手来的用户先给他一份能直接上传的表，别让人自己猜列名；
          旁边再给个一键试算——只想看看效果的人不必先下载再上传 */}
      <p className="hint sample-row">
        <a href="/api/quote/sample">{Trans.t('quote.sample')}</a>
        <span className="sep">{Trans.t('quote.sampleSep')}</span>
        <button className="btn ghost tiny" disabled={busy} onClick={runSample}>
          {busy ? Trans.t('quote.running') : Trans.t('quote.sampleRun')}
        </button>
      </p>

      {/* 这一行只放「开始估价」。下载报价单属于"已经有结果之后"的动作，
          统一放在报价表下面那一行，不在这里再摆一个灰着的重复按钮。 */}
      <div className="field">
        <button className="btn" disabled={!file || busy} onClick={() => run()}>
          {busy ? Trans.t('quote.running') : Trans.t('quote.run')}
        </button>
      </div>

      {job && job.status === 'running' && <QuoteProgress job={job} />}

      {err && <div className="error-box">{err}</div>}

      {/* 没结果时下半屏原来是一片空白。放三步说明 + 覆盖平台：既解释了产品，也把版面撑起来。
          出了结果就收起来，让报价表当主角。 */}
      {!quote && !job && (
        <div className="steps">
          {([1, 2, 3] as const).map((n) => (
            <div className="step" key={n}>
              <span className="step-no">{n}</span>
              <div>
                <div className="step-title">{Trans.t(`quote.step${n}`)}</div>
                <div className="step-sub">{Trans.t(`quote.step${n}sub`)}</div>
              </div>
            </div>
          ))}
        </div>
      )}


      {quote && (
        <>
          <div className="summary">
            <span>{Trans.t('quote.sumPriced')}: <b>{quote.priced}/{quote.count}</b></span>
            {typeof quote.markupPercent === 'number'
              && <span>{Trans.t('quote.sumMarkup')}: <b>{quote.markupPercent}%</b></span>}
            <span>{Trans.t('quote.sumTotal')}: <b>{quote.total} {quote.currency}</b></span>
          </div>
          <QuoteTable quote={quote} canSeeCost={canSeeCost} fxMeta={fxMeta} />

          {/* 主操作只有一个：一键下单（免登录）。下载 xlsx 是次要动作，做成小号次级按钮，
              两者不再互相抢戏，也不再出现"（需登录）"这种把人挡在外面的字样。 */}
          <div className="field">
            <button className="btn accent" disabled={!quote.priced} onClick={placeOrder}>
              {Trans.t('quote.order')}
            </button>
            <button className="btn ghost tiny" disabled={!quote.priced} onClick={download}>
              {Trans.t('quote.download')}
            </button>
            {ordered && <span className="order-ok">{Trans.t('quote.orderDone')}</span>}
            {quote.priced
              ? <span className="hint">{Trans.t('quote.orderFree')}</span>
              : <span className="hint">{Trans.t('quote.orderNoPriced')}</span>}
          </div>
        </>
      )}

      {/* 历史详情：弹窗里看，关掉回到原位；不动当前的估价结果 */}
      {historyView && (
        <Modal
          title={`${Trans.t('history.title')} · ${historyView.fileName}`}
          onClose={() => setHistoryView(null)}
          wide
        >
          <QuoteTable quote={historyView.quote} canSeeCost={canSeeCost} fxMeta={fxMeta} />
        </Modal>
      )}

      {orderOpen && quote && (
        <OrderDialog
          historyId={quote.historyId}
          claimToken={quote.claimToken}
          total={quote.total}
          currency={quote.currency}
          lines={quote.count}
          priced={quote.priced}
          defaults={{ name: user?.name ?? '', email: user?.email ?? '' }}
          onClose={() => setOrderOpen(false)}
          onDone={() => { setOrderOpen(false); setOrdered(true); }}
        />
      )}

      <div className="history">
        <div className="history-head">{Trans.t('history.title')}</div>
        {!user && <p className="hint">{Trans.t('history.needLogin')}</p>}
        {user && (history === null || history.length === 0) && (
          <p className="hint">{Trans.t('history.empty')}</p>
        )}
        {user && history !== null && history.length > 0 && (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  {/* 同订单列表：最常点的动作放行首 */}
                  <th />
                  <th>{Trans.t('history.col.time')}</th>
                  <th>{Trans.t('history.col.file')}</th>
                  <th className="num">{Trans.t('history.col.lines')}</th>
                  <th className="num">{Trans.t('history.col.total')}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td>
                      <button className="btn ghost row-view" onClick={() => openHistory(h.id)}>
                        {Trans.t('history.view')}
                      </button>
                    </td>
                    <td className="nowrap">{h.createdAt.replace('T', ' ').slice(0, 19)}</td>
                    <td>{h.fileName}</td>
                    <td className="num">{h.priced}/{h.count}</td>
                    <td className="num">{h.total} {h.currency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
